import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { interval, Observable, Subject, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ParamMapHelper } from '../helpers/param-map.helper';
import { StorageService } from './storage.service';
import { DateRangeScope } from '../types/date-range-scope.type';
import { DataAvailabilityService } from './data-availability.service';
import { DateRange } from '../types/date-range.type';
import { NavigationService } from './navigation.service';
import { DataAvailabilityInfo } from '../types/data-availability-info.type';
import { DateHelper, DateRangeFormat } from '../helpers/date.helper';
import { ApiService } from './api/api.service';

@Injectable({ providedIn: 'root' })
export class CalendarService {
  private dateRangeScope: DateRangeScope = DateRangeScope.Global;
  private getDatesWithData: () => Promise<DateRange[]>;
  private getDataAvailabilityInfo: () => Promise<DataAvailabilityInfo>;
  private _selectedDateRange: DateRange;
  private _dataAvailability: DataAvailabilityInfo;
  private selectedDateRangeChangedSubject = new Subject<DateRange>();
  private selectedDateChangedSubject = new Subject<Date>();
  private dataAvailabilityChangedSubject = new Subject<DataAvailabilityInfo>();
  private today: Date;

  get selectedDateRange(): DateRange {
    return this._selectedDateRange;
  }

  get selectedDate(): Date {
    return this._selectedDateRange?.start;
  }

  get selectedDateRangeChanged$(): Observable<DateRange> {
    return this.selectedDateRangeChangedSubject.asObservable();
  }

  get selectedDateChanged$(): Observable<Date> {
    return this.selectedDateChangedSubject.asObservable();
  }

  get dataAvailability(): DataAvailabilityInfo {
    return this._dataAvailability;
  }

  get dataAvailabilityChanged$(): Observable<DataAvailabilityInfo> {
    return this.dataAvailabilityChangedSubject.asObservable();
  }

  get selectedDateRangeLabel(): string {
    if (this.selectedDateRange == null) {
      return '';
    }
    return this.selectedDateRange.isSingleDay() ? DateHelper.getDayLabel(this.selectedDateRange.start) : this.selectedDateRange.format(DateRangeFormat.DateOnly);
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private storageService: StorageService,
    private dataAvailabilityService: DataAvailabilityService,
    private navigationService: NavigationService,
    private apiService: ApiService,
  ) {
    interval(60000).pipe(takeUntilDestroyed(), switchMap(async () => await this.checkDateChange())).subscribe();
  }

  private async checkDateChange(): Promise<void> {
    const today = DateHelper.startOfToday();
    if (this.today == null) {
      return;
    }
    if (this.today.getTime() !== today.getTime()) {
      this.dataAvailabilityService.clear();
      await this.initializeCalendar(this.dateRangeScope, this.getDatesWithData, this.getDataAvailabilityInfo);
    }
  }

  public async initializeCalendar(dateRangeScope: DateRangeScope = DateRangeScope.Global, getDatesWithData?: () => Promise<DateRange[]>, getDataAvailabilityInfo?: () => Promise<DataAvailabilityInfo>): Promise<void> {
    this.today = DateHelper.startOfToday();
    if (this.dateRangeScope != dateRangeScope) {
      this.dateRangeScope = dateRangeScope;
      this._selectedDateRange = null;
    }
    this.getDatesWithData = getDatesWithData;
    this.getDataAvailabilityInfo = getDataAvailabilityInfo;
    const queryDateRange = ParamMapHelper.parseInterval(this.activatedRoute.snapshot.queryParamMap);
    const storageDateRange = this.storageService.getDateRange(this.dateRangeScope);
    this._dataAvailability = await this.getDataAvailability();
    this.dataAvailabilityChangedSubject.next(this._dataAvailability);
    const dataAvailabilityDateRange = this._dataAvailability?.lastDate != null ? new DateRange(this._dataAvailability.lastDate) : null;
    const dateRange = queryDateRange ?? storageDateRange ?? this._selectedDateRange ?? dataAvailabilityDateRange ?? new DateRange(DateHelper.startOfToday());
    await this.selectDateRange(dateRange);
  }

  private async getDataAvailability(): Promise<DataAvailabilityInfo> {
    return this.getDatesWithData != null ? this.createDataAvailability(await this.getDatesWithData())
      : this.getDataAvailabilityInfo != null ? await this.getDataAvailabilityInfo()
      : this.dateRangeScope === DateRangeScope.Simulator ? this.createDataAvailability(await this.apiService.emulatorMonitoring.getDatesWithData())
      : this.dateRangeScope === DateRangeScope.DumpViewer ? this.createDataAvailability(await this.apiService.dumpViewer.getDatesWithData())
      : await this.dataAvailabilityService.getDataAvailability();
  }

  private createDataAvailability(datesWithData: DateRange[]): DataAvailabilityInfo {
    return new DataAvailabilityInfo(datesWithData, datesWithData.length > 0);
  }

  public async selectDateRange(dateRange: DateRange): Promise<void> {
    this._selectedDateRange = dateRange;
    await this.navigationService.addQueryParams(ParamMapHelper.createIntervalParams(dateRange));
    this.storageService.setDateRange(this.dateRangeScope, dateRange);
    this.selectedDateRangeChangedSubject.next(this.selectedDateRange);
    this.selectedDateChangedSubject.next(this.selectedDate);
  }
}
